import './MessagesCard.scss';
import { AvatarMini, Dropdown, Tooltip } from '../../Common/';
import type { DropdownItemConfig } from '../../Common/';
import { AssignedTo } from '../../types/Todos';
import MessageBody from './subcomponents/MessageBody/MessageBody';
import {
  IconCommentsNew,
  IconTaskNew,
  IconToDo,
  IconMore,
  IconMention,
  IconEmailOut,
  IconEditNew,
  IconTrashCan,
} from 'components/Icons';
import { prefixClass } from 'lib/utils';
import IconButton from '../../Common/IconButton/IconButton';
import classNames from 'classnames';
import { Attachment } from '../../../Queue/Chat/Chat';
import MessageAttachments from '../../../Queue/common/MessageAttachments/MessageAttachments';
import { useAuth } from 'context/useAuthContext';
import type { CheckListItemProps } from '../../Common/CheckList/CheckList';
import {
  messagePreviewDetectors,
  type EmailMetadata,
  type MessageDetailsContent,
} from './subcomponents/MessageContent/analyzeMessageContent';
import { useFlags } from 'launchdarkly-react-client-sdk';
import type { AskRequest } from '@hone-automation/common';

export interface MessagesCardProps {
  userId: string;
  assignedTo: AssignedTo;
  mention?: boolean;
  sentEmail?: boolean;
  self?: boolean;
  message: string;
  internalMessage?: boolean;
  attachments?: Attachment[];
  userName: string;
  userType?: string;
  dateTime?: string;
  users?: any[];
  fallbackUser?: {
    userId: string;
    name: string;
  };
  skipMentionFormatting?: boolean;
  checklist?: CheckListItemProps[];
  details?: MessageDetailsContent;
  isFirstMessage?: boolean;
  emailMetadata?: EmailMetadata;
  request?: AskRequest;
  // Edit/Delete operations
  messageId?: string;
  canEdit?: boolean;
  canDelete?: boolean;
  isEditing?: boolean;
  showDeleteModal?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onSaveEdit?: (content: string) => void;
  onCancelEdit?: () => void;
  onConfirmDelete?: () => void;
  onCancelDelete?: () => void;
}

/* 
 * TO-DO implement reply controls, when available, in useMessageActions.tsx 
 *
const baseControls = [
  { icon: <IconTaskNew />, tooltip: 'Create a To-do', onClick: () => undefined },
  { icon: <IconToDo />, tooltip: 'Create a Checklist', onClick: () => undefined },
  { icon: <IconCommentsNew />, tooltip: 'Start a new Conversation', onClick: () => undefined },
];
*/

const MessagesCard = ({
  userId,
  assignedTo,
  mention,
  sentEmail,
  self,
  message,
  internalMessage,
  attachments,
  userName,
  userType,
  dateTime,
  users,
  fallbackUser,
  skipMentionFormatting,
  checklist,
  details,
  isFirstMessage,
  emailMetadata,
  request,
  messageId,
  canEdit,
  canDelete,
  isEditing,
  showDeleteModal,
  onEdit,
  onDelete,
  onSaveEdit,
  onCancelEdit,
  onConfirmDelete,
  onCancelDelete,
}: MessagesCardProps) => {
  const prefix = prefixClass('q2-messages-card');

  // Show dropdown menu if user can edit or delete
  const showOptionsMenu = self && (canEdit || canDelete);
  const { enableImagePreviewQueue } = useFlags();

  return (
    <div
      className={classNames(prefix(), {
        internal: internalMessage,
        'external-email': messagePreviewDetectors.isValidEmailMetadata(emailMetadata),
        'is-first-message': isFirstMessage,
        'is-first-message-internal': isFirstMessage && internalMessage,
        'is-first-message-email': isFirstMessage && messagePreviewDetectors.isValidEmailMetadata(emailMetadata),
      })}
    >
      <div className={prefix('flags')}>
        <AvatarMini id={assignedTo.id} type={assignedTo.type} name={assignedTo.name} />
        {mention && (
          <IconButton
            icon={<IconMention />}
            tooltip="Mention"
            onClick={() => undefined}
            className={prefix('flags__mention')}
          />
        )}
        {emailMetadata?.status === 'Sent' && (
          <IconButton
            icon={<IconEmailOut />}
            size="small"
            tooltip="Sent as external Email"
            onClick={() => undefined}
            className={prefix('flags__email')}
          />
        )}
      </div>
      <div className={prefix('body')}>
        <MessageBody
          userName={userName}
          userType={userType}
          internal={internalMessage}
          dateTime={dateTime}
          /* replyControls={baseControls} */
          message={message}
          users={users}
          fallbackUser={fallbackUser}
          skipMentionFormatting={skipMentionFormatting}
          attachments={attachments}
          checklist={checklist}
          details={details}
          isFirstMessage={isFirstMessage}
          emailMetadata={emailMetadata}
          request={request}
          isEditing={isEditing}
          onSaveEdit={onSaveEdit}
          onCancelEdit={onCancelEdit}
        />
        {!enableImagePreviewQueue && attachments && <MessageAttachments attachments={attachments} />}
      </div>
      <div className={prefix('options')}>
        {showOptionsMenu &&
          (emailMetadata?.status === 'Sent' ? (
            <Tooltip content="Sent emails cannot be edited or deleted" position="left">
              <span style={{ display: 'inline-flex' }}>
                <IconButton icon={<IconMore />} onClick={() => {}} disabled />
              </span>
            </Tooltip>
          ) : (
            <Dropdown
              trigger={<IconButton icon={<IconMore />} tooltip="More options" onClick={() => {}} />}
              items={[
                ...(canEdit
                  ? [
                      {
                        id: 'edit',
                        label: 'Edit',
                        icon: <IconEditNew />,
                        onClick: () => onEdit?.(),
                      },
                    ]
                  : []),
                ...(canDelete
                  ? [
                      {
                        id: 'delete',
                        label: 'Delete',
                        icon: <IconTrashCan />,
                        onClick: () => onDelete?.(),
                        danger: true,
                      },
                    ]
                  : []),
              ]}
              position="bottom"
              alignment="end"
              itemAlignment="right"
            />
          ))}
      </div>
    </div>
  );
};

export default MessagesCard;
